import { useMutation } from "@apollo/client";
import { useErrorHandler } from "TranscriptsV3/hooks";
import {
  updateTranscriptStudentGradeGradingPeriodMappingMutation,
  upsertTranscriptStudentGradeMappingMutation,
} from "../modules/mutations";
import { useGradeLevelsChangesContext } from "../context/GradeLevelsChangesContext";
import { useGradeLevelsContext } from "../context/GradeLevelsContext";
import { useCallback } from "react";
import _ from "lodash";
import {
  checkForErrors,
  buildRemovedGradeMappings,
  buildAddedGradeMappings,
  buildGradeIdToMapIdLookup,
  buildGradingPeriodMappings,
} from "../modules/utils";
import {
  getStudentTranscriptGradeLevelsQuery,
  getTranscriptEntitiesQuery,
} from "../modules/queries";
import { useDispatch } from "Hooks";
import { setToastMsg } from "Login/modules/LoginModule";

const useUpdateMapping = () => {
  const onError = useErrorHandler();
  const dispatch = useDispatch();
  const {
    studentsMergedDataRef,
    allRemoved,
    setAllErrors,
  } = useGradeLevelsChangesContext();
  const { setLoading } = useGradeLevelsContext();

  const [upsertTranscriptStudentGradeMappingMutateFn] = useMutation(
    upsertTranscriptStudentGradeMappingMutation,
    {
      onError,
    }
  );

  const [
    updateTranscriptStudentGradeGradingPeriodMappingMutateFn,
  ] = useMutation(updateTranscriptStudentGradeGradingPeriodMappingMutation, {
    onError,
  });

  const validateAndShowErrors = useCallback(
    ({ studentWiseData }) => {
      const errors = checkForErrors({ studentWiseData });

      if (errors.size > 0) {
        setAllErrors(errors);
        dispatch(
          setToastMsg({
            type: "error",
            msg: "transcript:fill_required_fields_error",
          })
        );
        return true;
      }

      return false;
    },
    [dispatch, setAllErrors]
  );

  const upsertGradeMappings = useCallback(
    async ({ removed, added }) => {
      const { data } = await upsertTranscriptStudentGradeMappingMutateFn({
        variables: {
          input: { removed, added },
        },
      });

      const records = _.get(
        data,
        "transcript.upsertTranscriptStudentGradeMapping.records",
        []
      );

      return buildGradeIdToMapIdLookup(records);
    },
    [upsertTranscriptStudentGradeMappingMutateFn]
  );

  const updateGradingPeriodMappings = useCallback(
    async ({ addedGradingPeriods, removedGradingPeriods }) => {
      await updateTranscriptStudentGradeGradingPeriodMappingMutateFn({
        variables: {
          input: {
            added: addedGradingPeriods,
            removed: removedGradingPeriods,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          getStudentTranscriptGradeLevelsQuery,
          getTranscriptEntitiesQuery,
        ],
      });
    },
    [updateTranscriptStudentGradeGradingPeriodMappingMutateFn]
  );

  return useCallback(async () => {
    const studentWiseData = Object.fromEntries(studentsMergedDataRef.current);

    if (_.isEmpty(studentWiseData)) {
      return false;
    }

    const hasErrors = validateAndShowErrors({ studentWiseData });
    if (hasErrors) {
      return true;
    }

    const removed = buildRemovedGradeMappings(allRemoved);
    const added = buildAddedGradeMappings(studentWiseData);

    setLoading(true);

    const gradeIdToGradeMapId = await upsertGradeMappings({ removed, added });

    const {
      addedGradingPeriods,
      removedGradingPeriods,
    } = buildGradingPeriodMappings({ studentWiseData, gradeIdToGradeMapId });

    await updateGradingPeriodMappings({
      addedGradingPeriods,
      removedGradingPeriods,
    });

    setLoading(false);

    return false;
  }, [
    allRemoved,
    setLoading,
    studentsMergedDataRef,
    upsertGradeMappings,
    updateGradingPeriodMappings,
    validateAndShowErrors,
  ]);
};

export default useUpdateMapping;
